import { createStyles, Grid, makeStyles, Paper, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import laqusImf from 'assets/laqus-imf-digital-1x.png';
import { CpfMask } from 'components/masks';
import { Button, MdSpace, SmSpace, TextField } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { RootState } from 'store';
import { useBaseStyles } from './baseStyle';
import { validaCpf } from 'utils/validation';

interface RouteParams {
  conviteId: string;
}

const useStyles = makeStyles(() =>
  createStyles({
    MuiIcon: {
      fill: '#E5E5E5',
      marginRight: 10,
    },
    MuiIconActive: {
      fill: '#00E08E',
      marginRight: 10,
    },
  })
);

const mapStateToProps = (state: RootState) => ({
  usuarioApi: state.usuarioApi,
});

type NovoUsuarioProps = RouteComponentProps<RouteParams> & ReturnType<typeof mapStateToProps>;

const NovoUsuario: React.FC<NovoUsuarioProps> = (props) => {
  const baseClasses = useBaseStyles();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { usuarioApi } = props;
  const { conviteId } = props.match.params;

  const [nome, setNome] = useState<string>('');
  const [cpf, setCpf] = useState<string>('');
  const [cpfValido, setCpfValido] = useState(false);
  const [allowCpfInput, setAllowCpfInput] = useState<boolean>(false);
  const [senha, setSenha] = useState<string>('');
  const [senhaConfirmacao, setSenhaConfirmacao] = useState<string>('');
  const [allowRegister, setAllowRegister] = useState<boolean>(false);

  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLElement>) => {
      event.preventDefault();
      setSubmitting(true);
      try {
        const result = await usuarioApi.postCadastrarUsuario(dispatch, conviteId, {
          nome,
          senha,
          cpf: cpf.replace(/\D/g, ''),
        });
        if (result) {
          props.history.push('/login');
        } else {
          setAllowRegister(false);
        }
      } catch (error) {
        console.log(error);
        setAllowRegister(false);
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, usuarioApi, conviteId, props, nome, senha, cpf, cpfValido]
  );

  const getRequest = useCallback(async () => {
    try {
      const result = await usuarioApi.getConvite(dispatch, conviteId, true);
      setAllowRegister(!!result);
      if (result) {
        setNome(result.nome);
        setCpf(result.cpf ?? "");
        setAllowCpfInput((result.cpf ?? "") !== "");
      }
    } catch (error) {
      console.log(error);
      setAllowRegister(false);
    }
  }, [dispatch, usuarioApi, conviteId]);

  useEffect(() => {
    getRequest();
  }, [getRequest]);

  useEffect(() => setCpfValido(!!cpf && validaCpf(cpf)), [cpf, cpfValido]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ margin: '40px 0' }}>
          <Paper className={baseClasses.paper}>
            <Grid container justify="center">
              <Grid item>
                <img src={laqusImf} alt="Logo Laqus IMF digital" />
              </Grid>
              <Grid item sm={12}>
                <MdSpace />
                <Typography align="center" variant="h2">
                  {allowRegister ? `Seja bem-vindo!` : `Olá!`}
                </Typography>
                <MdSpace />
                {allowRegister ? (
                  <Typography align="center" variant="body2">
                    {`Crie uma senha para completar seu cadastro.`}
                  </Typography>
                ) : (
                  <>
                    <Typography align="center" variant="body2">
                      {`Este link não existe ou já expirou.`}
                      <br />
                      <a style={{ color: 'inherit' }} href="/login">
                        {'Clique aqui'}
                      </a>
                      &nbsp;{`para ser redirecionado para o login.`}
                    </Typography>
                    <SmSpace />
                    <Typography align="center" variant="body2">
                      {`Caso isso seja um problema, envie um e-mail para`}&nbsp;
                      <a style={{ color: '#00E08E' }} href="mailto:admin@admin.com">
                        {'admin@admin.com'}
                      </a>
                    </Typography>
                  </>
                )}
                <MdSpace />
              </Grid>
              <Grid item sm={12}>
                {allowRegister && (
                  <form onSubmit={handleSubmit}>
                    <TextField label="Nome" value={nome} disabled onChange={() => void 0} />
                    <SmSpace />
                    <TextField
                      label="CPF *"
                      value={CpfMask(cpf)}
                      disabled={allowCpfInput}
                      onChange={setCpf}
                      error={!!cpf && !cpfValido}
                      errorMessage={'CPF inválido'}
                    />
                    <SmSpace />
                    <TextField label="Senha" type="password" required value={senha} onChange={setSenha} />
                    <SmSpace />
                    <TextField
                      label="Confirmação da Senha"
                      type="password"
                      required
                      value={senhaConfirmacao}
                      onChange={setSenhaConfirmacao}
                    />
                    <MdSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon
                        className={!!senha && senha === senhaConfirmacao ? classes.MuiIconActive : classes.MuiIcon}
                      />
                      <Typography align="left" variant="body2">{`As senhas devem ser iguais`}</Typography>
                    </Grid>
                    <SmSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon className={senha.match(/.{10,}/) ? classes.MuiIconActive : classes.MuiIcon} />
                      <Typography align="left" variant="body2">{`Mínimo 10 caracteres`}</Typography>
                    </Grid>
                    <SmSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon className={senha.match(/[A-Z]+/) ? classes.MuiIconActive : classes.MuiIcon} />
                      <Typography align="left" variant="body2">{`Letra maíuscula`}</Typography>
                    </Grid>
                    <SmSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon className={senha.match(/[a-z]+/) ? classes.MuiIconActive : classes.MuiIcon} />
                      <Typography align="left" variant="body2">{`Letra minúscula`}</Typography>
                    </Grid>
                    <SmSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon className={senha.match(/[0-9]+/) ? classes.MuiIconActive : classes.MuiIcon} />
                      <Typography align="left" variant="body2">{`Número`}</Typography>
                    </Grid>
                    <SmSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon
                        className={senha.match(/[^a-zA-Z 0-9]+/g) ? classes.MuiIconActive : classes.MuiIcon}
                      />
                      <Typography align="left" variant="body2">{`Caractere especial`}</Typography>
                    </Grid>
                    <MdSpace />
                    <Grid>
                      <Button
                        type="submit"
                        disabled={
                          submitting ||
                          !senha ||
                          !senha.match(/.{8,}/) ||
                          !senha.match(/[A-Z]+/) ||
                          !senha.match(/[a-z]+/) ||
                          !senha.match(/[0-9]+/) ||
                          !senha.match(/[^a-zA-Z 0-9]+/g) ||
                          senha !== senhaConfirmacao ||
                          !cpfValido
                        }
                        variant="contained"
                        color="primary"
                        style={{ margin: '0 auto', display: 'block' }}
                      >
                        {'Enviar'}
                      </Button>
                    </Grid>
                  </form>
                )}
              </Grid>
            </Grid>
          </Paper>
        </div>
      </div>
    </>
  );
};

export default connect(mapStateToProps)(withRouter(NovoUsuario));
